import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Space,
  Typography,
  Checkbox,
  message,
  Card,
  Drawer,
  InputNumber,
  Divider,
  Steps,
  Row,
  Col,
  Avatar,
  Tag,
  Spin,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  FileTextOutlined,
  DownloadOutlined,
  EyeOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  UserAddOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useAuth0 } from "@auth0/auth0-react";
import { useOrganization } from "../contexts/organization-context";
import { useSubscription } from "../contexts/subscription-context";
import apiClient from "../utils/axios";
import { loadTermsAndConditions, loadPrivacyPolicy, loadBillingAgreement, LEGAL_DOCUMENTS } from "../utils/legal-documents";
import { generateTextPDF } from "../utils/pdf-generator";
import { SubscriptionSetup } from "./SubscriptionSetup";

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

// Add CSS animation for highlighting checkboxes
const pulseAnimation = `
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(255, 169, 64, 0.7);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(255, 169, 64, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(255, 169, 64, 0);
    }
  }
`;

// Inject the CSS animation
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = pulseAnimation;
  document.head.appendChild(style);
}

interface CreateOrganizationModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

interface DocumentViewerProps {
  title: string;
  content: string;
  open: boolean;
  onClose: () => void;
  onViewed: () => void;
  loading?: boolean;
  documentId: string;
}

interface FacilityInvite {
  facilityName: string;
  facilityId: string;
  emails: string[];
  rawValue?: string;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  title,
  content,
  open,
  onClose,
  onViewed,
  loading = false,
  documentId,
}) => {
  const [downloading, setDownloading] = useState(false);
  const [downloadingText, setDownloadingText] = useState(false);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const element = document.getElementById(`document-content-${documentId}`);
      if (!element) {
        throw new Error('Document content element not found');
      }

      // Wait a bit to ensure content is fully rendered
      await new Promise(resolve => setTimeout(resolve, 100));

      // Enhanced html2canvas options for better compatibility
      const canvas = await html2canvas(element, {
        scale: 1.5, // Reduced scale to avoid memory issues
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: element.scrollWidth,
        height: element.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
      });

      // Validate canvas
      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        throw new Error('Failed to generate canvas from content');
      }

      const imgData = canvas.toDataURL('image/png', 0.95); // Slightly compressed
      
      // Validate image data
      if (!imgData || imgData === 'data:,') {
        throw new Error('Failed to generate image data from canvas');
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`${title.replace(/\s+/g, '_')}.pdf`);
      message.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF with html2canvas:', error);
      
      // Try fallback method with text-based PDF
      try {
        console.log('Attempting fallback PDF generation...');
        generateTextPDF(title, content);
        message.success('PDF downloaded successfully (text format)');
      } catch (fallbackError) {
        console.error('Fallback PDF generation also failed:', fallbackError);
        
        // More specific error messages
        let errorMessage = 'Failed to generate PDF';
        if (error instanceof Error) {
          if (error.message.includes('canvas')) {
            errorMessage = 'Failed to render document content. Please try the text-only version.';
          } else if (error.message.includes('image')) {
            errorMessage = 'Failed to process document image. Please try again.';
          } else if (error.message.includes('memory') || error.message.includes('size')) {
            errorMessage = 'Document too large to process. Please try with a smaller document.';
          }
        }
        
        message.error(errorMessage);
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadTextPDF = async () => {
    setDownloadingText(true);
    try {
      generateTextPDF(title, content);
      message.success('Text-based PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating text PDF:', error);
      message.error('Failed to generate text-based PDF');
    } finally {
      setDownloadingText(false);
    }
  };

  const handleClose = () => {
    onViewed();
    onClose();
  };

  return (
    <Drawer
      title={
        <Space>
          <FileTextOutlined />
          {title}
        </Space>
      }
      open={open}
      onClose={handleClose}
      width="80%"
      zIndex={2000}
      extra={
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownloadPDF}
            loading={downloading}
            disabled={loading || !content}
          >
            Download PDF
          </Button>
          <Button
            icon={<FileTextOutlined />}
            onClick={handleDownloadTextPDF}
            loading={downloadingText}
            disabled={loading || !content}
          >
            Text PDF
          </Button>
          <Button
            type="primary"
            onClick={handleClose}
            size="large"
          >
            Close
          </Button>
        </Space>
      }
    >
      <div
        id={`document-content-${documentId}`}
        style={{
          padding: '24px',
          backgroundColor: '#fff',
          lineHeight: '1.6',
          fontSize: '14px',
        }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text>Loading document...</Text>
          </div>
        ) : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children }) => (
                <Title level={1} style={{ color: '#1890ff', marginTop: '32px', marginBottom: '16px' }}>
                  {children}
                </Title>
              ),
              h2: ({ children }) => (
                <Title level={2} style={{ color: '#1890ff', marginTop: '24px', marginBottom: '12px' }}>
                  {children}
                </Title>
              ),
              h3: ({ children }) => (
                <Title level={3} style={{ marginTop: '20px', marginBottom: '8px' }}>
                  {children}
                </Title>
              ),
              p: ({ children }) => (
                <Text style={{ display: 'block', marginBottom: '12px' }}>
                  {children}
                </Text>
              ),
              ul: ({ children }) => (
                <ul style={{ marginBottom: '12px', paddingLeft: '20px' }}>
                  {children}
                </ul>
              ),
              li: ({ children }) => (
                <li style={{ marginBottom: '4px' }}>
                  {children}
                </li>
              ),
              strong: ({ children }) => (
                <Text strong>{children}</Text>
              ),
              em: ({ children }) => (
                <Text italic>{children}</Text>
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        )}
      </div>
    </Drawer>
  );
};

export const CreateOrganizationModal: React.FC<CreateOrganizationModalProps> = ({
  open,
  onCancel,
  onSuccess,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { refreshOrganizations } = useOrganization();
  const { refreshSubscriptionData } = useSubscription();
  const [form] = Form.useForm();
  const [creating, setCreating] = useState(false);
  
  // Step management
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  
  // Organization data
  const [organizationName, setOrganizationName] = useState('');
  const [createdOrgId, setCreatedOrgId] = useState<string | null>(null);
  
  // Facility creation state
  const [facilityCount, setFacilityCount] = useState(1);
  const [facilityNames, setFacilityNames] = useState<string[]>(['']);
  
  // Subscription state
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [loadingSubscriptionData, setLoadingSubscriptionData] = useState(false);
  
  // User invitation state
  const [facilityInvites, setFacilityInvites] = useState<FacilityInvite[]>([]);
  
  // Document viewing state
  const [termsViewed, setTermsViewed] = useState(false);
  const [privacyViewed, setPrivacyViewed] = useState(false);
  const [billingViewed, setBillingViewed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [billingAccepted, setBillingAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showBilling, setShowBilling] = useState(false);
  
  // Document content state
  const [termsContent, setTermsContent] = useState('');
  const [privacyContent, setPrivacyContent] = useState('');
  const [billingContent, setBillingContent] = useState('');
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [loadingPrivacy, setLoadingPrivacy] = useState(false);
  const [loadingBilling, setLoadingBilling] = useState(false);

  const steps = [
    {
      title: 'Organization',
      description: 'Setup & legal',
      icon: <TeamOutlined />,
    },
    {
      title: 'Facilities',
      description: 'Add locations',
      icon: <PlusOutlined />,
    },
    {
      title: 'Subscription',
      description: 'Billing setup',
      icon: <CreditCardOutlined />,
    },
    {
      title: 'Team Invites',
      description: 'Invite users',
      icon: <UserAddOutlined />,
    },
  ];

  // Step 1 validation
  const canProceedStep1 = organizationName.trim().length >= 2 && 
    termsViewed && privacyViewed && billingViewed && 
    termsAccepted && privacyAccepted && billingAccepted;

  // Step 2 validation  
  const canProceedStep2 = facilityNames.every(name => name.trim().length >= 2);

  // Step 3 validation - subscription must be completed
  const [subscriptionCompleted, setSubscriptionCompleted] = useState(false);

  // Load document functions (keeping existing code)
  const handleShowTerms = async () => {
    if (!termsContent) {
      setLoadingTerms(true);
      try {
        const content = await loadTermsAndConditions();
        setTermsContent(content);
      } catch (error) {
        message.error('Failed to load Terms and Conditions');
      } finally {
        setLoadingTerms(false);
      }
    }
    setShowTerms(true);
  };

  const handleShowPrivacy = async () => {
    if (!privacyContent) {
      setLoadingPrivacy(true);
      try {
        const content = await loadPrivacyPolicy();
        setPrivacyContent(content);
      } catch (error) {
        message.error('Failed to load Privacy Policy');
      } finally {
        setLoadingPrivacy(false);
      }
    }
    setShowPrivacy(true);
  };

  const handleShowBilling = async () => {
    if (!billingContent) {
      setLoadingBilling(true);
      try {
        const content = await loadBillingAgreement();
        setBillingContent(content);
      } finally {
        setLoadingBilling(false);
      }
    }
    setShowBilling(true);
  };

  // Create organization and facilities (moved to after step 2)
  const createOrganizationAndFacilities = async () => {
    try {
      setCreating(true);
      
      const token = await getAccessTokenSilently();
      
      // Prepare facilities data
      const facilities = facilityNames
        .filter(name => name.trim())
        .map(name => ({ name: name.trim() }));
      
      const response = await apiClient.post("/api/access/create-organization", {
        name: organizationName.trim(),
        facilities: facilities,
        legal_acceptance: {
          terms_accepted: termsAccepted,
          privacy_accepted: privacyAccepted,
          billing_accepted: billingAccepted,
          terms_version: LEGAL_DOCUMENTS.TERMS.version,
          privacy_version: LEGAL_DOCUMENTS.PRIVACY.version,
          billing_version: LEGAL_DOCUMENTS.BILLING.version
        }
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      const createdFacilitiesData = response.data.facilities || [];
      setCreatedOrgId(response.data.id);
      
      // Initialize facility invites for step 4
      const initialInvites = createdFacilitiesData.map((facility: any) => ({
        facilityName: facility.name,
        facilityId: facility.id,
        emails: [],
        rawValue: ''
      }));
      setFacilityInvites(initialInvites);
      
      message.success("Organization and facilities created successfully!");
      
      // Fetch subscription data for the subscription step
      await fetchSubscriptionData(response.data.id);
      
      return true;
    } catch (error: any) {
      console.error("Error creating organization:", error);
      message.error(
        error.response?.data?.detail || "Failed to create organization"
      );
      return false;
    } finally {
      setCreating(false);
    }
  };

  // Fetch subscription data for the subscription step
  const fetchSubscriptionData = async (orgId: string) => {
    const maxRetries = 3;
    const baseDelay = 1000; // 1 second
    
    try {
      setLoadingSubscriptionData(true);
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const token = await getAccessTokenSilently();
          
          const response = await apiClient.get(`/api/organization/${orgId}/subscription`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          
          setSubscriptionData(response.data);
          return; // Success, exit the retry loop
          
        } catch (error: any) {
          console.error(`Error fetching subscription data (attempt ${attempt}/${maxRetries}):`, error);
          
          // If it's a 403 error and we have more attempts, wait and retry
          if (error.response?.status === 403 && attempt < maxRetries) {
            const delay = baseDelay * Math.pow(2, attempt - 1); // Exponential backoff
            console.log(`Retrying in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
          
          // If it's the last attempt or not a 403 error, show error and break
          message.error("Failed to load subscription data");
          break;
        }
      }
    } finally {
      setLoadingSubscriptionData(false);
    }
  };

  // Step navigation
  const nextStep = async () => {
    if (currentStep < steps.length - 1) {
      // If moving from step 2 to step 3, create org and facilities first
      if (currentStep === 1) {
        const success = await createOrganizationAndFacilities();
        if (!success) {
          return; // Don't proceed if creation failed
        }
      }
      
      setCompletedSteps(prev => [...prev, currentStep]);
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle facility count change
  const handleFacilityCountChange = (count: number) => {
    setFacilityCount(count);
    const newFacilityNames = [...facilityNames];
    
    // Add empty names for new facilities
    while (newFacilityNames.length < count) {
      newFacilityNames.push('');
    }
    
    // Remove extra names if count decreased
    if (newFacilityNames.length > count) {
      newFacilityNames.splice(count);
    }
    
    setFacilityNames(newFacilityNames);
  };

  // Handle facility name change
  const handleFacilityNameChange = (index: number, name: string) => {
    const newNames = [...facilityNames];
    newNames[index] = name;
    setFacilityNames(newNames);
  };

  // Handle email list changes for facility invitations
  const handleEmailChange = (facilityIndex: number, rawValue: string) => {
    const newInvites = [...facilityInvites];
    // Store the raw value for display, but also process emails for validation
    newInvites[facilityIndex].rawValue = rawValue;
    newInvites[facilityIndex].emails = rawValue
      .split('\n')
      .map(email => email.trim())
      .filter(email => email && email.includes('@')); // Only keep valid-looking emails
    setFacilityInvites(newInvites);
  };

  // Handle subscription success
  const handleSubscriptionSuccess = async () => {
    message.success("Subscription created successfully!");
    
    // Mark subscription as completed
    setSubscriptionCompleted(true);
    
    // Move to invitations step immediately
    nextStep();
  };

  // Complete the setup (send invitations)
  const handleCompleteSetup = async () => {
    try {
      setCreating(true);
      
      // Send invitations for facilities that have emails
      const token = await getAccessTokenSilently();
      
      let totalInvitesSent = 0;
      let totalInvitesFailed = 0;
      
      for (const invite of facilityInvites) {
        if (invite.emails.length > 0) {
          // Send invitations for each email in this facility
          for (const email of invite.emails) {
            try {
              await apiClient.post(`/api/facility/${invite.facilityId}/members/invite`, {
                email: email,
                role: 'Member' // Default role for setup invitations
              }, {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              });
              totalInvitesSent++;
            } catch (error: any) {
              console.error(`Failed to invite ${email} to ${invite.facilityName}:`, error);
              totalInvitesFailed++;
            }
          }
        }
      }
      
      // Refresh contexts sequentially to ensure proper state updates
      console.log("Refreshing organization data...");
      await refreshOrganizations();
      
      // Small delay to ensure organization context has fully updated
      await new Promise(resolve => setTimeout(resolve, 500));
      
      console.log("Refreshing subscription data...");
      await refreshSubscriptionData(true);
      
      // Another small delay to ensure subscription context has fully updated
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Show appropriate success message
      let successMessage = "Setup completed successfully!";
      if (totalInvitesSent > 0) {
        successMessage += ` Sent ${totalInvitesSent} invitation${totalInvitesSent !== 1 ? 's' : ''}.`;
      }
      if (totalInvitesFailed > 0) {
        successMessage += ` ${totalInvitesFailed} invitation${totalInvitesFailed !== 1 ? 's' : ''} failed - you can resend them from the Facilities page.`;
      }
      
      message.success(successMessage);
      
      // Close modal and trigger success callback
      handleCancel();
      onSuccess();
      
    } catch (error: any) {
      console.error("Error completing setup:", error);
      message.error("Setup completed but some invitations failed. You can invite users later from the Facilities page.");
      
      // Still close and refresh since org was created
      try {
        await refreshOrganizations();
        await new Promise(resolve => setTimeout(resolve, 500));
        await refreshSubscriptionData(true);
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (refreshError) {
        console.error("Error refreshing contexts:", refreshError);
      }
      
      handleCancel();
      onSuccess();
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = () => {
    // Reset all state
    setCurrentStep(0);
    setCompletedSteps([]);
    setOrganizationName('');
    setCreatedOrgId(null);
    setFacilityCount(1);
    setFacilityNames(['']);
    setSubscriptionData(null);
    setFacilityInvites([]);
    setTermsViewed(false);
    setPrivacyViewed(false);
    setBillingViewed(false);
    setTermsAccepted(false);
    setPrivacyAccepted(false);
    setBillingAccepted(false);
    setSubscriptionCompleted(false);
    form.resetFields();
    onCancel();
  };

  // Render Step 1: Organization Details & Legal
  const renderStep1 = () => (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>Organization Information</Title>
      
      <Form.Item
        label="Organization Name"
        required
        style={{ marginBottom: 24 }}
      >
        <Input 
          value={organizationName}
          onChange={(e) => setOrganizationName(e.target.value)}
          placeholder="Enter your organization name" 
          size="large"
        />
      </Form.Item>

      <Card size="small" style={{ backgroundColor: "#e6f7ff", marginBottom: 24 }}>
        <Text style={{ color: "#1890ff" }}>
          ✨ Your organization includes a 14-day free trial
        </Text>
      </Card>

      <Title level={5} style={{ marginBottom: 16 }}>Legal Documents</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Please review and accept these legal documents <strong>on behalf of your organization</strong>:
      </Text>

      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Card size="small">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Text strong>Terms and Conditions</Text>
              {termsViewed && <Text type="success" style={{ marginLeft: 8 }}>✓ Viewed</Text>}
            </div>
            <Space>
              <Button
                icon={<EyeOutlined />}
                onClick={handleShowTerms}
                size="small"
                loading={loadingTerms}
              >
                View
              </Button>
              <Tooltip 
                title={!termsViewed ? "Please view the document first by clicking the 'View' button" : ""}
                placement="top"
              >
                <div style={{ 
                  padding: termsViewed && !termsAccepted ? '4px 8px' : '0',
                  borderRadius: '4px',
                  backgroundColor: termsViewed && !termsAccepted ? '#fff7e6' : 'transparent',
                  border: termsViewed && !termsAccepted ? '2px solid #ffa940' : 'none',
                  animation: termsViewed && !termsAccepted ? 'pulse 2s infinite' : 'none',
                }}>
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    disabled={!termsViewed}
                  >
                    Accept
                  </Checkbox>
                </div>
              </Tooltip>
            </Space>
          </div>
        </Card>

        <Card size="small">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Text strong>Privacy Policy</Text>
              {privacyViewed && <Text type="success" style={{ marginLeft: 8 }}>✓ Viewed</Text>}
            </div>
            <Space>
              <Button
                icon={<EyeOutlined />}
                onClick={handleShowPrivacy}
                size="small"
                loading={loadingPrivacy}
              >
                View
              </Button>
              <Tooltip 
                title={!privacyViewed ? "Please view the document first by clicking the 'View' button" : ""}
                placement="top"
              >
                <div style={{ 
                  padding: privacyViewed && !privacyAccepted ? '4px 8px' : '0',
                  borderRadius: '4px',
                  backgroundColor: privacyViewed && !privacyAccepted ? '#fff7e6' : 'transparent',
                  border: privacyViewed && !privacyAccepted ? '2px solid #ffa940' : 'none',
                  animation: privacyViewed && !privacyAccepted ? 'pulse 2s infinite' : 'none',
                }}>
                  <Checkbox
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    disabled={!privacyViewed}
                  >
                    Accept
                  </Checkbox>
                </div>
              </Tooltip>
            </Space>
          </div>
        </Card>

        <Card size="small">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Text strong>Billing Agreement</Text>
              {billingViewed && <Text type="success" style={{ marginLeft: 8 }}>✓ Viewed</Text>}
            </div>
            <Space>
              <Button
                icon={<EyeOutlined />}
                onClick={handleShowBilling}
                size="small"
                loading={loadingBilling}
              >
                View
              </Button>
              <Tooltip 
                title={!billingViewed ? "Please view the document first by clicking the 'View' button" : ""}
                placement="top"
              >
                <div style={{ 
                  padding: billingViewed && !billingAccepted ? '4px 8px' : '0',
                  borderRadius: '4px',
                  backgroundColor: billingViewed && !billingAccepted ? '#fff7e6' : 'transparent',
                  border: billingViewed && !billingAccepted ? '2px solid #ffa940' : 'none',
                  animation: billingViewed && !billingAccepted ? 'pulse 2s infinite' : 'none',
                }}>
                  <Checkbox
                    checked={billingAccepted}
                    onChange={(e) => setBillingAccepted(e.target.checked)}
                    disabled={!billingViewed}
                  >
                    Accept
                  </Checkbox>
                </div>
              </Tooltip>
            </Space>
          </div>
        </Card>
      </Space>
    </div>
  );

  // Render Step 2: Facility Setup
  const renderStep2 = () => (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>Facility Setup</Title>
      <Paragraph type="secondary">
        Create the facilities for your organization. You can always add more facilities later.
      </Paragraph>
      
      <Form.Item
        label="How many facilities do you want to create?"
        style={{ marginBottom: 24 }}
      >
        <InputNumber
          min={1}
          max={100}
          value={facilityCount}
          onChange={(value) => handleFacilityCountChange(value || 1)}
          size="large"
          style={{ width: '100%' }}
        />
      </Form.Item>

      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {facilityNames.map((name, index) => (
          <Card key={index} size="small" style={{ backgroundColor: '#fafafa' }}>
            <Row gutter={16} align="middle">
              <Col span={4}>
                <Avatar 
                  style={{ backgroundColor: '#1890ff' }}
                  icon={<TeamOutlined />}
                />
              </Col>
              <Col span={20}>
                <Input
                  value={name}
                  onChange={(e) => handleFacilityNameChange(index, e.target.value)}
                  placeholder={`Enter facility ${index + 1} name`}
                  size="large"
                />
              </Col>
            </Row>
          </Card>
        ))}
      </Space>
    </div>
  );

  // Render Step 3: Subscription
  const renderStep3 = () => (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>Choose Your Subscription Plan</Title>
      <Paragraph type="secondary">
        Your organization and facilities have been created! Now choose your billing plan to activate your subscription.
      </Paragraph>

      {loadingSubscriptionData ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Loading subscription options...</Text>
          </div>
        </div>
      ) : subscriptionData ? (
        <SubscriptionSetup
          organizationId={createdOrgId!}
          subscriptionData={subscriptionData}
          onSubscriptionSuccess={handleSubscriptionSuccess}
          showTitle={false}
          embedded={true}
        />
      ) : (
        <Card style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Title level={4} type="secondary">Unable to load subscription data</Title>
          <Text type="secondary">Please try again or contact support.</Text>
        </Card>
      )}


    </div>
  );

     // Render Step 4: User Invitations  
   const renderStep4 = () => {
     const totalInvitations = facilityInvites.reduce((sum, invite) => sum + invite.emails.length, 0);
     
     return (
       <div>
         <Title level={4} style={{ marginBottom: 16 }}>
           Invite Team Members 
           {totalInvitations > 0 && (
             <Tag color="blue" style={{ marginLeft: 8 }}>
               {totalInvitations} invitation{totalInvitations !== 1 ? 's' : ''} ready
             </Tag>
           )}
         </Title>
         <Paragraph type="secondary">
           Invite users to your facilities. You can always invite more people later from the Organization → Facilities page.
         </Paragraph>

         {facilityInvites.length === 0 ? (
           <Card style={{ textAlign: 'center', padding: '40px 20px' }}>
             <TeamOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
             <Title level={4} type="secondary">No facilities available</Title>
             <Text type="secondary">There was an issue loading facilities for invitations.</Text>
           </Card>
         ) : (
           <Space direction="vertical" style={{ width: '100%' }} size="large">
             {facilityInvites.map((invite, index) => (
               <Card 
                 key={index} 
                 title={
                   <Space>
                     <Avatar 
                       size="small"
                       style={{ backgroundColor: '#1890ff' }}
                       icon={<TeamOutlined />}
                     />
                     {invite.facilityName}
                     {invite.emails.length > 0 && (
                       <Tag color="green">
                         {invite.emails.length} invite{invite.emails.length !== 1 ? 's' : ''}
                       </Tag>
                     )}
                   </Space>
                 }
                 size="small"
               >
                 <Form.Item
                   label="Email addresses (one per line)"
                   help="Enter email addresses of users you want to invite to this facility"
                   style={{ marginBottom: 16 }}
                 >
                   <Input.TextArea
                     rows={4}
                     placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
                     value={invite.rawValue || invite.emails.join('\n')}
                     onChange={(e) => {
                       handleEmailChange(index, e.target.value);
                     }}
                   />
                 </Form.Item>
               </Card>
             ))}
           </Space>
         )}

         <Card 
           size="small" 
           style={{ backgroundColor: "#f6ffed", marginTop: 24, border: "1px solid #b7eb8f" }}
         >
           <Row gutter={16} align="middle">
             <Col>
               <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 20 }} />
             </Col>
             <Col flex={1}>
               <Text style={{ color: "#389e0d" }}>
                 <strong>No pressure!</strong> You can skip invitations now and invite team members anytime from the Organization → Facilities page.
               </Text>
             </Col>
           </Row>
         </Card>
       </div>
     );
   };

  // Render step content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderStep1();
      case 1:
        return renderStep2();
      case 2:
        return renderStep3();
      case 3:
        return renderStep4();
      default:
        return renderStep1();
    }
  };

  // Render navigation buttons
  const renderNavigation = () => (
    <Row justify="space-between" style={{ marginTop: 32 }}>
      <Col>
        {currentStep > 0 && (
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={prevStep}
            disabled={creating || loadingSubscriptionData}
            size="large"
          >
            Back
          </Button>
        )}
      </Col>
      <Col>
        <Space size="middle">
          <Button onClick={handleCancel} disabled={creating || loadingSubscriptionData} size="large">
            Cancel
          </Button>
          
          {currentStep < steps.length - 1 ? (
            <Button
              type="primary"
              icon={creating || loadingSubscriptionData ? null : <ArrowRightOutlined />}
              onClick={nextStep}
              disabled={
                (currentStep === 0 && !canProceedStep1) ||
                (currentStep === 1 && !canProceedStep2) ||
                (currentStep === 2 && !subscriptionCompleted) ||
                creating ||
                loadingSubscriptionData
              }
              loading={creating || loadingSubscriptionData}
              size="large"
            >
              {creating && currentStep === 1 ? 'Creating Organization...' : 'Next'}
            </Button>
          ) : (
            <Button
              type="primary"
              icon={creating ? null : <CheckCircleOutlined />}
              onClick={handleCompleteSetup}
              loading={creating}
              size="large"
            >
              {creating ? 'Sending Invitations...' : 'Complete Setup'}
            </Button>
          )}
        </Space>
      </Col>
    </Row>
  );

  return (
    <>
      <Modal
        title={
          <div>
            <Title level={3} style={{ margin: 0 }}>Create New Organization</Title>
            <Text type="secondary">Step {currentStep + 1} of {steps.length}</Text>
          </div>
        }
        open={open}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
        width={800}
      >
        <Steps 
          current={currentStep} 
          style={{ marginBottom: 32 }}
          size="small"
          responsive={false}
        >
          {steps.map((step, index) => (
            <Step
              key={index}
              title={<span style={{ fontSize: 13, fontWeight: 500 }}>{step.title}</span>}
              description={<span style={{ fontSize: 12, color: '#8c8c8c' }}>{step.description}</span>}
              icon={completedSteps.includes(index) ? <CheckCircleOutlined /> : step.icon}
              status={
                completedSteps.includes(index) 
                  ? 'finish' 
                  : index === currentStep 
                    ? 'process' 
                    : 'wait'
              }
            />
          ))}
        </Steps>

        {renderStepContent()}
        {renderNavigation()}
      </Modal>

      {/* Document viewers remain the same */}
      <DocumentViewer
        title={LEGAL_DOCUMENTS.TERMS.title}
        content={termsContent}
        open={showTerms}
        onClose={() => setShowTerms(false)}
        onViewed={() => setTermsViewed(true)}
        loading={loadingTerms}
        documentId="terms"
      />

      <DocumentViewer
        title={LEGAL_DOCUMENTS.PRIVACY.title}
        content={privacyContent}
        open={showPrivacy}
        onClose={() => setShowPrivacy(false)}
        onViewed={() => setPrivacyViewed(true)}
        loading={loadingPrivacy}
        documentId="privacy"
      />

      <DocumentViewer
        title={LEGAL_DOCUMENTS.BILLING.title}
        content={billingContent}
        open={showBilling}
        onClose={() => setShowBilling(false)}
        onViewed={() => setBillingViewed(true)}
        loading={loadingBilling}
        documentId="billing"
      />
    </>
  );
}; 